/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import api from '../utils/api';
import { peekNextNumber, advanceNumber } from '../utils/docNumbering';
import useFocusRecord from '../utils/useFocusRecord';
import ExcelImport from '../components/ExcelImport';
import RequestCancellationButton from '../components/RequestCancellationButton';
import SalesPerformanceTable from '../components/sales-invoices/SalesPerformanceTable';
import InvoiceFilters from '../components/sales-invoices/InvoiceFilters';
import InvoicePrintView from '../components/sales-invoices/InvoicePrintView';
import { resolvePreparedBy, resolveApprovers } from '../utils/signatures';
import SortableTh from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import SearchableSelect from '@/components/ui/SearchableSelect';
import * as XLSX from 'xlsx';
import { hasPermission, applyViewScope } from '../utils/permissions';
import FindDuplicatesButton from '../components/FindDuplicatesButton';
import ApprovalBar from '../components/ApprovalBar';
import { ApprovalStatusBadge } from '../components/DraftWatermark';

export default function SalesInvoices() {
  const { user } = useAuth();
  const { formatAmount, companySettings } = useCurrency();
  const { selectedFy } = useFiscalYear();
  // VAT rate is sourced from Company Settings (admin-editable). Falls back
  // to 10% (Bahrain default) if no value present or 0% (legacy seed).
  const companyVatRate = (companySettings?.tax_percentage || 10);
  const [printingInvoice, setPrintingInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);  // NEW: For sub-category dropdown
  const [brands, setBrands] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [models, setModels] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [productSearchTerm, setProductSearchTerm] = useState({});
  const [showProductDropdown, setShowProductDropdown] = useState({});
  const [partNumberSearchTerm, setPartNumberSearchTerm] = useState({});  // NEW: For part number search
  const [showPartNumberDropdown, setShowPartNumberDropdown] = useState({});  // NEW: For part number dropdown
  const [showExcelImport, setShowExcelImport] = useState(false);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCustomerId, setFilterCustomerId] = useState('all');
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  // New filters — one per visible table column (matches headers shown in the
  // invoice list: Invoice # / Date / Due Date / Customer / Sales Rep / Total
  // / Status / Approval / Overdue).
  const [dueDateFilter, setDueDateFilter] = useState({ start: '', end: '' });
  const [filterSalesRepId, setFilterSalesRepId] = useState('all');
  const [totalRange, setTotalRange] = useState({ min: '', max: '' });
  const [filterApproval, setFilterApproval] = useState('all');   // draft / pending / approved / rejected / all
  const [filterOverdue, setFilterOverdue] = useState('all');     // overdue / not_overdue / all
  const [bucketFilter, setBucketFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('bucket') || '';
  });
  const [salesPerformance, setSalesPerformance] = useState([]);
  // Customer-advance suggestions (against the entered po_no for the picked customer)
  const [openAdvances, setOpenAdvances] = useState([]);
  const [appliedAdvanceIds, setAppliedAdvanceIds] = useState([]);
  // Tracks how much of advance_payment was auto-applied from open advances,
  // so we can subtract it cleanly when the PO/customer changes.
  const [autoAppliedAmount, setAutoAppliedAmount] = useState(0);
  // Map of product_id → array of available serial number objects, loaded on
  // demand when a product is picked on a line item.
  const [availableSerialsMap, setAvailableSerialsMap] = useState({});

  // Delivery Note linkage: when admin picks a DN, customer + items + serials
  // auto-populate. On invoice save, the linked DN is marked 'invoiced'.
  const [openDeliveryNotes, setOpenDeliveryNotes] = useState([]);
  const [linkedDnNumber, setLinkedDnNumber] = useState('');

  const fetchAvailableSerials = async (productId) => {
    if (!productId) return;
    try {
      const res = await api.get(`/products/${productId}/available-serials`);
      setAvailableSerialsMap((prev) => ({ ...prev, [productId]: res.data?.items || [] }));
    } catch (e) {
      console.error('Failed to fetch available serials for', productId, e);
    }
  };

  // Load OPEN delivery notes whenever the New Invoice form is opened so the
  // user can quickly link one. Editing an existing invoice doesn't trigger a
  // refetch (the invoice is already linked at this point).
  useEffect(() => {
    if (showForm && !editingInvoice) {
      api.get('/delivery-notes/open')
        .then((res) => setOpenDeliveryNotes(res.data || []))
        .catch(() => setOpenDeliveryNotes([]));
      // Auto-fill the invoice number from the company's numbering rule.
      // The user can still edit the field manually before saving.
      peekNextNumber('invoice').then((n) => {
        if (n) setFormData((prev) => prev.invoice_number ? prev : { ...prev, invoice_number: n });
      });
    }
  }, [showForm, editingInvoice]);

  // Sync the global Fiscal Year selection → the list-level date filter.
  // When the user picks FY 2025 in the header, every invoice with
  // invoice_date outside 2025-01-01…2025-12-31 is hidden client-side.
  // Users can still override by editing the date pickers in the filter bar.
  useEffect(() => {
    if (!selectedFy) return;
    setDateFilter({ start: selectedFy.start_date, end: selectedFy.end_date });
  }, [selectedFy?.id]);

  const applyDeliveryNote = async (dnNumber) => {
    if (!dnNumber) {
      setLinkedDnNumber('');
      return;
    }
    try {
      const res = await api.get(`/delivery-notes/${encodeURIComponent(dnNumber)}`);
      const dn = res.data;
      if (!dn) return;
      setLinkedDnNumber(dn.dn_number);
      // Auto-populate items + customer; price fields left blank for the user.
      setFormData((prev) => ({
        ...prev,
        customer_id: dn.customer_id || prev.customer_id,
        customer_name: dn.customer_name || prev.customer_name,
        do_no: dn.dn_number, // stamp DN# onto the existing "DO No" meta field
        po_no: dn.customer_po_no || prev.po_no,
        po_date: dn.customer_po_date || prev.po_date,
        project: dn.project || prev.project,
        items: (dn.items || []).map((it) => ({
          product_id: it.product_id || '',
          product_name: it.product_name || '',
          part_number: it.part_number || '',
          category: it.category || '',
          sub_category: it.sub_category || '',
          brand: it.brand || '',
          division: it.division || '',
          model: '',
          quantity: it.quantity || 1,
          unit_price: 0, // user fills this in
          total: 0,
          serial_numbers: it.serial_numbers || [],
          warranty_months: it.warranty_months || 0,
        })),
      }));
      // Pre-warm serial pools so the line UI shows them
      for (const it of (dn.items || [])) {
        if (it.product_id) fetchAvailableSerials(it.product_id);
      }
    } catch (e) {
      console.error(e);
      alert('Failed to load Delivery Note');
    }
  };

  const toggleItemSerial = (index, serial) => {
    setFormData((prev) => {
      const items = [...prev.items];
      const cur = Array.isArray(items[index].serial_numbers) ? items[index].serial_numbers : [];
      const exists = cur.includes(serial);
      items[index] = {
        ...items[index],
        serial_numbers: exists ? cur.filter((s) => s !== serial) : [...cur, serial],
      };
      // Auto-sync quantity with number of selected serials (the user's "if I
      // pick 3 serials I sold 3 of them" intent — they can still override).
      const selCount = items[index].serial_numbers.length;
      if (selCount > 0) {
        items[index].quantity = selCount;
        items[index].total = (items[index].unit_price || 0) * selCount;
      }
      return { ...prev, items };
    });
    // recalc totals after state update
    setTimeout(() => calculateTotals(formData.items), 0);
  };

  // Form state
  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    customer_id: '',
    customer_name: '',
    sales_rep_id: user?.id || '',
    sales_rep_name: user?.name || '',
    items: [{ 
      product_id: '',
      product_name: '', 
      part_number: '',  // NEW
      category: '', 
      sub_category: '',  // NEW
      brand: '', 
      division: '', 
      model: '', 
      quantity: 1, 
      unit_price: 0, 
      total: 0,
      serial_numbers: [],
      warranty_months: 0,
    }],
    subtotal: 0,
    advance_payment: 0,
    vat_percentage: companyVatRate,
    vat_amount: 0,
    total_amount: 0,
    payment_status: 'Pending',
    notes: '',
    do_no: '',
    po_no: '',
    po_date: '',
    project: '',
    discount: 0,
    vat_category: '',   // Empty = auto-classify on the VAT return
  });

  useEffect(() => {
    fetchData();
    
    // Fetch sales performance for users who can view sales reports
    if (hasPermission(user, 'sales_reports', 'view')) {
      fetchSalesPerformance();
    }
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.product-dropdown-container')) {
        setShowProductDropdown({});
      }
      if (!event.target.closest('.part-number-dropdown-container')) {
        setShowPartNumberDropdown({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedFy?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoicesRes, customersRes, usersRes, categoriesRes, subcategoriesRes, brandsRes, divisionsRes, modelsRes, productsRes] = await Promise.all([
        api.get('/sales/invoices'),
        api.get('/customers'),
        api.get('/users'),
        api.get('/master-data/categories'),
        api.get('/master-data/subcategories'),  // NEW
        api.get('/master-data/brands'),
        api.get('/master-data/divisions'),
        api.get('/master-data/models'),
        api.get('/products')
      ]);
      
      setInvoices(invoicesRes.data || []);
      setCustomers(customersRes.data || []);
      setSalesReps(usersRes.data || []);  // Show ALL users (admin, sales, support)
      setCategories(categoriesRes.data || []);
      setSubcategories(subcategoriesRes.data || []);  // NEW
      setBrands(brandsRes.data || []);
      setDivisions(divisionsRes.data || []);
      setModels(modelsRes.data || []);
      setProducts(productsRes.data || []);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesPerformance = async () => {
    try {
      // view_scope='own' restricts a user to records they own (regardless of
      // their role). Admins/super-admins always see everything.
      const ownScope = (user?.view_scope || 'all') === 'own' &&
                       user?.role !== 'admin' && user?.role !== 'super_admin';
      const params = new URLSearchParams();
      if (ownScope) params.set('sales_rep_id', user.id);
      // Scope the per-rep performance to the active Fiscal Year so the
      // numbers match every other FY-aware widget on the page.
      if (selectedFy?.start_date) params.set('start_date', selectedFy.start_date);
      if (selectedFy?.end_date) params.set('end_date', selectedFy.end_date);
      const qs = params.toString();
      const url = `/sales/reports/salesreps${qs ? `?${qs}` : ''}`;

      const response = await api.get(url);
      setSalesPerformance(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  // Auto-apply any unapplied customer advances against the entered (customer + PO).
  // Whenever (customer_id, po_no) changes:
  //  1. Subtract the previously auto-applied amount from advance_payment.
  //  2. Fetch new open advances for this customer+PO.
  //  3. Add their total to advance_payment, remember the IDs for link-on-save.
  // The user can still manually edit advance_payment afterwards.
  useEffect(() => {
    if (!showForm) {
      return;
    }
    const customerId = formData.customer_id;
    const poNo = (formData.po_no || '').trim();

    // Helper: reset to no auto-applied advance.
    const clearAutoApplied = () => {
      if (appliedAdvanceIds.length === 0 && openAdvances.length === 0) return;
      setOpenAdvances([]);
      setAppliedAdvanceIds([]);
      const currentAdv = parseFloat(formData.advance_payment) || 0;
      const restored = parseFloat(Math.max(0, currentAdv - autoAppliedAmount).toFixed(3));
      setAutoAppliedAmount(0);
      // Recompute totals with the manual advance only
      setFormData((prev) => {
        const subtotal = (prev.items || []).reduce((s, i) => s + (i.total || 0), 0);
        const vatAmt = subtotal * ((prev.vat_percentage || 0) / 100);
        const totalAmt = Math.max(0, subtotal + vatAmt - restored);
        return {
          ...prev,
          advance_payment: restored,
          subtotal: parseFloat(subtotal.toFixed(3)),
          vat_amount: parseFloat(vatAmt.toFixed(3)),
          total_amount: parseFloat(totalAmt.toFixed(3)),
        };
      });
    };

    if (!customerId || !poNo) {
      clearAutoApplied();
      return;
    }

    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/payments/advances/open?customer_id=${encodeURIComponent(customerId)}&po_number=${encodeURIComponent(poNo)}`);
        const items = res.data?.items || [];
        const newTotal = parseFloat((items.reduce((s, a) => s + (a.received_amount || 0), 0)).toFixed(3));
        const newIds = items.map((a) => a.id);
        setOpenAdvances(items);
        setAppliedAdvanceIds(newIds);
        // Adjust advance_payment by the delta vs. previously auto-applied total.
        setFormData((prev) => {
          const currentAdv = parseFloat(prev.advance_payment) || 0;
          const manualAdv = Math.max(0, currentAdv - autoAppliedAmount); // user-typed portion
          const newAdvancePayment = parseFloat((manualAdv + newTotal).toFixed(3));
          const subtotal = (prev.items || []).reduce((s, i) => s + (i.total || 0), 0);
          const vatAmt = subtotal * ((prev.vat_percentage || 0) / 100);
          const totalAmt = Math.max(0, subtotal + vatAmt - newAdvancePayment);
          return {
            ...prev,
            advance_payment: newAdvancePayment,
            subtotal: parseFloat(subtotal.toFixed(3)),
            vat_amount: parseFloat(vatAmt.toFixed(3)),
            total_amount: parseFloat(totalAmt.toFixed(3)),
          };
        });
        setAutoAppliedAmount(newTotal);
      } catch (e) {
        // Silently leave the form alone on lookup failure.
      }
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, formData.customer_id, formData.po_no]);

  const calculateTotals = (items, advance, discountArg) => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const adv = advance !== undefined ? parseFloat(advance) || 0 : parseFloat(formData.advance_payment) || 0;
    const disc = discountArg !== undefined ? parseFloat(discountArg) || 0 : parseFloat(formData.discount) || 0;
    // VAT is calculated on full subtotal (not after discount / advance)
    const vat_amount = subtotal * (formData.vat_percentage / 100);
    // Discount and advance both deduct from the total INCLUDING VAT.
    const total_amount = Math.max(0, subtotal + vat_amount - disc - adv);

    setFormData(prev => ({
      ...prev,
      subtotal: parseFloat(subtotal.toFixed(3)),
      advance_payment: parseFloat(adv.toFixed(3)),
      discount: parseFloat(disc.toFixed(3)),
      vat_amount: parseFloat(vat_amount.toFixed(3)),
      total_amount: parseFloat(total_amount.toFixed(3))
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    // Cascading logic: clear dependent fields when parent changes
    if (field === 'division') {
      updatedItems[index].category = '';
      updatedItems[index].sub_category = '';  // Clear sub-category when division changes
      updatedItems[index].brand = '';
      updatedItems[index].model = '';
    } else if (field === 'category') {
      updatedItems[index].sub_category = '';  // Clear sub-category when category changes
      updatedItems[index].brand = '';
      updatedItems[index].model = '';
    } else if (field === 'brand') {
      updatedItems[index].model = '';
    }
    
    // Calculate line total
    if (field === 'quantity' || field === 'unit_price') {
      const qty = field === 'quantity' ? parseFloat(value) || 0 : updatedItems[index].quantity;
      const price = field === 'unit_price' ? parseFloat(value) || 0 : updatedItems[index].unit_price;
      updatedItems[index].total = parseFloat((qty * price).toFixed(3));
    }
    
    setFormData(prev => ({ ...prev, items: updatedItems }));
    calculateTotals(updatedItems);
  };

  const handleProductSelect = (index, product) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      product_id: product.id,
      product_name: product.name,
      part_number: product.part_number || '',  // NEW
      category: product.category || '',
      sub_category: product.sub_category || '',  // NEW
      brand: product.brand || '',
      division: product.division || '',
      model: product.model || '',
      unit_price: product.price || 0,
      total: (product.price || 0) * updatedItems[index].quantity,
      serial_numbers: [],
    };
    setFormData(prev => ({ ...prev, items: updatedItems }));
    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
    setProductSearchTerm(prev => ({ ...prev, [index]: product.name }));
    fetchAvailableSerials(product.id);
  };

  const handleProductSearch = (index, value) => {
    setProductSearchTerm(prev => ({ ...prev, [index]: value }));
    handleItemChange(index, 'product_name', value);
    setShowProductDropdown(prev => ({ ...prev, [index]: true }));
  };

  const getFilteredProducts = (index) => {
    const searchTerm = productSearchTerm[index] || '';
    if (!searchTerm) return products;
    return products.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.part_number && product.part_number.toLowerCase().includes(searchTerm.toLowerCase()))  // NEW: Search by part number
    );
  };

  // NEW: Handler for Part Number search
  const handlePartNumberSearch = (index, value) => {
    setPartNumberSearchTerm(prev => ({ ...prev, [index]: value }));
    handleItemChange(index, 'part_number', value);
    setShowPartNumberDropdown(prev => ({ ...prev, [index]: true }));
  };

  // NEW: Get filtered products by part number
  const getFilteredProductsByPartNumber = (index) => {
    const searchTerm = partNumberSearchTerm[index] || '';
    if (!searchTerm) return products.filter(p => p.part_number); // Only show products with part numbers
    return products.filter(product =>
      product.part_number && product.part_number.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // NEW: Handler for Part Number selection
  const handlePartNumberSelect = (index, product) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      product_id: product.id,
      part_number: product.part_number || '',
      product_name: product.name,
      category: product.category || '',
      sub_category: product.sub_category || '',
      brand: product.brand || '',
      division: product.division || '',
      model: product.model || '',
      unit_price: product.price || 0,
      total: (product.price || 0) * updatedItems[index].quantity,
      serial_numbers: [],
    };
    setFormData(prev => ({ ...prev, items: updatedItems }));
    setShowPartNumberDropdown(prev => ({ ...prev, [index]: false }));
    setPartNumberSearchTerm(prev => ({ ...prev, [index]: product.part_number }));
    setProductSearchTerm(prev => ({ ...prev, [index]: product.name }));
    fetchAvailableSerials(product.id);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { 
        product_id: '',
        product_name: '', 
        part_number: '',
        category: '', 
        sub_category: '',
        brand: '', 
        division: '', 
        model: '', 
        quantity: 1, 
        unit_price: 0, 
        total: 0,
        serial_numbers: [],
        warranty_months: 0,
      }]
    }));
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: updatedItems }));
    calculateTotals(updatedItems);
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData(prev => ({
      ...prev,
      customer_id: customerId,
      customer_name: customer?.name || ''
    }));
  };

  const handleSalesRepChange = (salesRepId) => {
    const salesRep = salesReps.find(s => s.id === salesRepId);
    setFormData(prev => ({
      ...prev,
      sales_rep_id: salesRepId,
      sales_rep_name: salesRep?.name || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const invoiceNumber = editingInvoice
        ? editingInvoice.invoice_number
        : formData.invoice_number;
      if (editingInvoice) {
        await api.put(`/sales/invoices?invoice_number=${encodeURIComponent(editingInvoice.invoice_number)}`, formData);
      } else {
        await api.post('/sales/invoices', formData);
        // Bump the per-company invoice counter so the next "+ New Invoice"
        // peek returns the next value.
        advanceNumber('invoice', invoiceNumber);
      }

      // Link any advances the user applied to this invoice draft. Best-effort:
      // failures here do not invalidate the saved invoice.
      if (appliedAdvanceIds.length && invoiceNumber) {
        await Promise.all(
          appliedAdvanceIds.map((advId) =>
            api.post(`/payments/${advId}/apply`, { invoice_number: invoiceNumber }).catch(() => null)
          )
        );
      }

      // If this invoice was built from a Delivery Note, flip the DN status to
      // 'invoiced' and store the back-link. Best-effort — failure does not
      // invalidate the saved invoice.
      if (!editingInvoice && linkedDnNumber && invoiceNumber) {
        await api
          .post(`/delivery-notes/${encodeURIComponent(linkedDnNumber)}/mark-invoiced`, { invoice_number: invoiceNumber })
          .catch((err) => console.error('Failed to mark DN invoiced:', err));
      }

      setShowForm(false);
      setEditingInvoice(null);
      resetForm();
      setAppliedAdvanceIds([]);
      setOpenAdvances([]);
      setAutoAppliedAmount(0);
      setLinkedDnNumber('');
      fetchData();
    } catch (error) {
      console.error('Failed to save invoice:', error);
      alert(error.response?.data?.detail || 'Failed to save invoice');
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      customer_id: '',
      customer_name: '',
      sales_rep_id: user?.id || '',
      sales_rep_name: user?.name || '',
      items: [{ 
        product_id: '',
        product_name: '', 
        part_number: '',  // NEW
        category: '', 
        sub_category: '',  // NEW
        brand: '', 
        division: '', 
        model: '', 
        quantity: 1, 
        unit_price: 0, 
        total: 0,
        serial_numbers: [],
        warranty_months: 0,
      }],
      subtotal: 0,
      advance_payment: 0,
      vat_percentage: companyVatRate,
      vat_amount: 0,
      total_amount: 0,
      payment_status: 'Pending',
      notes: '',
      do_no: '',
      po_no: '',
      po_date: '',
      project: '',
      discount: 0,
      vat_category: '',
    });
    // Also clear per-line search state so previous invoice's product/part
    // number search text doesn't leak into the new invoice form.
    setProductSearchTerm({});
    setShowProductDropdown({});
    setPartNumberSearchTerm({});
    setShowPartNumberDropdown({});
  };

  // Handle Excel Export — exports invoices matching the current filters
  const handleExportExcel = () => {
    try {
      if (!filteredInvoices || filteredInvoices.length === 0) {
        alert('No invoices to export.');
        return;
      }
      // Currency code drives column headers so the export matches the active
      // company's settings (BHD for Bahrain, USD for US, etc.).
      const cur = companySettings?.currency || 'USD';

      // Summary sheet — one row per invoice
      const summaryRows = filteredInvoices.map((inv) => ({
        'Invoice Number': inv.invoice_number,
        'Invoice Date': formatDateDMY(inv.invoice_date),
        'Due Date': inv.due_date ? formatDateDMY(inv.due_date) : '',
        'Customer': inv.customer_name,
        'Sales Rep': inv.sales_rep_name || '',
        'PO No': inv.po_no || '',
        'DO No': inv.do_no || '',
        'Project': inv.project || '',
        [`Subtotal (${cur})`]: Number((inv.subtotal || 0).toFixed(3)),
        'VAT %': inv.vat_percentage ?? 10,
        [`VAT Amount (${cur})`]: Number((inv.vat_amount || 0).toFixed(3)),
        [`Total Incl. VAT (${cur})`]: Number(((inv.subtotal || 0) + (inv.vat_amount || 0)).toFixed(3)),
        [`Advance Received (${cur})`]: Number((inv.advance_payment || 0).toFixed(3)),
        [`Balance Payable (${cur})`]: Number((inv.total_amount || 0).toFixed(3)),
        'Payment Status': inv.payment_status || 'Pending',
        'Notes': inv.notes || '',
      }));

      // Items sheet — one row per line item across all filtered invoices
      const itemRows = [];
      filteredInvoices.forEach((inv) => {
        (inv.items || []).forEach((it, i) => {
          itemRows.push({
            'Invoice Number': inv.invoice_number,
            'Invoice Date': formatDateDMY(inv.invoice_date),
            'Customer': inv.customer_name,
            'Line #': i + 1,
            'Item Code / Part No': it.part_number || '',
            'Product': it.product_name || '',
            'Division': it.division || '',
            'Category': it.category || '',
            'Brand': it.brand || '',
            'Model': it.model || '',
            'Qty': it.quantity || 0,
            [`Unit Price (${cur})`]: Number((it.unit_price || 0).toFixed(3)),
            [`Line Total (${cur})`]: Number((it.total || 0).toFixed(3)),
          });
        });
      });

      // Totals row appended to summary
      const totals = filteredInvoices.reduce(
        (acc, inv) => ({
          subtotal: acc.subtotal + (inv.subtotal || 0),
          vat: acc.vat + (inv.vat_amount || 0),
          advance: acc.advance + (inv.advance_payment || 0),
          balance: acc.balance + (inv.total_amount || 0),
        }),
        { subtotal: 0, vat: 0, advance: 0, balance: 0 }
      );
      summaryRows.push({
        'Invoice Number': 'TOTAL',
        'Invoice Date': '',
        'Due Date': '',
        'Customer': '',
        'Sales Rep': '',
        'PO No': '',
        'DO No': '',
        'Project': '',
        [`Subtotal (${cur})`]: Number(totals.subtotal.toFixed(3)),
        'VAT %': '',
        [`VAT Amount (${cur})`]: Number(totals.vat.toFixed(3)),
        [`Total Incl. VAT (${cur})`]: Number((totals.subtotal + totals.vat).toFixed(3)),
        [`Advance Received (${cur})`]: Number(totals.advance.toFixed(3)),
        [`Balance Payable (${cur})`]: Number(totals.balance.toFixed(3)),
        'Payment Status': '',
        'Notes': '',
      });

      const wb = XLSX.utils.book_new();
      const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
      // Set column widths for readability
      wsSummary['!cols'] = [
        { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 28 }, { wch: 18 },
        { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 14 }, { wch: 8 },
        { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 24 },
      ];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Invoices');

      if (itemRows.length > 0) {
        const wsItems = XLSX.utils.json_to_sheet(itemRows);
        wsItems['!cols'] = [
          { wch: 14 }, { wch: 12 }, { wch: 28 }, { wch: 6 }, { wch: 18 },
          { wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
          { wch: 8 }, { wch: 14 }, { wch: 14 },
        ];
        XLSX.utils.book_append_sheet(wb, wsItems, 'Line Items');
      }

      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `sales_invoices_${stamp}.xlsx`);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export invoices: ' + (err.message || 'Unknown error'));
    }
  };

  // Handle Excel Import
  const handleExcelImport = async (importedInvoices) => {
    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      // Create a local copy of customers array that we'll update during import
      const localCustomers = [...customers];


      for (let index = 0; index < importedInvoices.length; index++) {
        const invoice = importedInvoices[index];
        try {
          
          // Find or create customer (search in localCustomers which gets updated during import)
          let customer = localCustomers.find(c => c.name.toLowerCase() === invoice.customer_name.toLowerCase());
          
          if (!customer) {
            // Create new customer with unique generated email (timestamp + index for uniqueness)
            const timestamp = Date.now();
            const sanitizedName = invoice.customer_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
            const generatedEmail = `${sanitizedName}_${timestamp}_${index}@imported.example.com`;
            
            try {
              const customerRes = await api.post('/customers', {
                name: invoice.customer_name,
                email: generatedEmail,
                contact_person: invoice.customer_name,
                phone: '',
                address: ''
              });
              customer = customerRes.data;
              // Add newly created customer to local array so subsequent invoices can find it
              localCustomers.push(customer);
            } catch (custErr) {
              console.error('Customer creation failed:', custErr);
              console.error('Customer creation error response:', custErr.response?.data);
              const errorMsg = custErr.response?.data?.detail || 
                              JSON.stringify(custErr.response?.data) || 
                              custErr.message;
              errors.push(`Invoice ${invoice.invoice_number}: Customer creation failed - ${errorMsg}`);
              errorCount++;
              continue;
            }
          } else {
          }

          // Calculate totals
          const subtotal = invoice.items.reduce((sum, item) => sum + item.total, 0);
          const vatAmount = subtotal * (companyVatRate / 100);
          const totalAmount = subtotal + vatAmount;

          // Create invoice
          const invoiceData = {
            invoice_number: invoice.invoice_number,
            invoice_date: invoice.invoice_date,
            customer_id: customer.id,
            customer_name: customer.name,
            sales_rep_id: user?.id || '',
            sales_rep_name: user?.name || '',
            items: invoice.items,
            subtotal: subtotal,
            vat_percentage: companyVatRate,
            vat_amount: vatAmount,
            total_amount: totalAmount,
            payment_status: 'Pending',
            notes: 'Imported from Excel'
          };

          
          await api.post('/sales/invoices', invoiceData);
          successCount++;
        } catch (err) {
          console.error('Error importing invoice:', invoice.invoice_number, err);
          console.error('Error details:', err.response?.data);
          errors.push(`Invoice ${invoice.invoice_number}: ${err.response?.data?.detail || err.message}`);
          errorCount++;
        }
      }

      
      if (errors.length > 0) {
      }

      const message = `Import complete!\nSuccessful: ${successCount}\nFailed: ${errorCount}${errors.length > 0 ? '\n\nFirst few errors:\n' + errors.slice(0, 5).join('\n') : ''}`;
      alert(message);
      
      setShowExcelImport(false);
      if (successCount > 0) {
        fetchData(); // Refresh the list
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Error during import: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({ advance_payment: 0, ...invoice });
    setShowForm(true);
    // Fetch available serials for each product on the invoice so the user can
    // see what's still in stock vs already locked to this invoice.
    (invoice.items || []).forEach((it) => {
      if (it.product_id) fetchAvailableSerials(it.product_id);
    });
  };

  // Diagnostics "Open ↗" deep-link: auto-open this invoice's edit modal when
  // ?focus=<id-or-invoice-number> is present.
  useFocusRecord(invoices, handleEdit);

  const handleDelete = async (invoice_number) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await api.delete(`/sales/invoices?invoice_number=${encodeURIComponent(invoice_number)}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      alert('Failed to delete invoice');
    }
  };

  // Filter invoices based on search and filters
  const filteredInvoices = applyViewScope(invoices, user).filter(invoice => {
    // Visibility is controlled by the Sales Invoices view permission on the
    // user (set via the User Rights matrix). When a user has view, they see
    // all invoices. Admins can scope a user down using the permissions
    // system instead of hard-coded role filters.

    // Search filter (invoice number, customer name, sales rep)
    const q = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' ||
      (invoice.invoice_number?.toString() || '').toLowerCase().includes(q) ||
      (invoice.customer_name || '').toLowerCase().includes(q) ||
      (invoice.sales_rep_name || '').toLowerCase().includes(q);
    
    // Status filter — "Pending" is interpreted broadly as "any outstanding"
    // (Pending OR Partially Paid), since a Partially Paid invoice still has
    // a pending balance. This keeps the filtered total in sync with the
    // Debtors Aging report.
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'Pending'
        ? invoice.payment_status === 'Pending' ||
          invoice.payment_status === 'Partially Paid'
        : invoice.payment_status === filterStatus);

    // Customer filter
    const matchesCustomer = filterCustomerId === 'all' || invoice.customer_id === filterCustomerId;

    // Sales Rep filter
    const matchesSalesRep = filterSalesRepId === 'all' || invoice.sales_rep_id === filterSalesRepId;

    // Approval status filter. Real DB values are: draft / pending_l1 /
    // pending_l2 / approved. The dropdown also offers a "pending_any"
    // convenience that matches either level.
    const apprStatus = invoice.approval_status || 'draft';
    const matchesApproval =
      filterApproval === 'all' ||
      (filterApproval === 'pending_any'
        ? apprStatus === 'pending_l1' || apprStatus === 'pending_l2'
        : apprStatus === filterApproval);

    // Outstanding-only quick toggle: hides Paid invoices
    const matchesOutstanding = !outstandingOnly || invoice.payment_status !== 'Paid';

    // Invoice Date range filter
    let matchesDate = true;
    if (dateFilter.start && invoice.invoice_date < dateFilter.start) matchesDate = false;
    if (dateFilter.end && invoice.invoice_date > dateFilter.end) matchesDate = false;

    // Due Date range filter (compute due date from customer credit period)
    let matchesDueDate = true;
    if (dueDateFilter.start || dueDateFilter.end) {
      const cust = customers.find((c) => c.id === invoice.customer_id);
      const creditDays = cust?.credit_period_days ?? 30;
      const inv = new Date(invoice.invoice_date);
      inv.setDate(inv.getDate() + creditDays);
      const iso = inv.toISOString().slice(0, 10);
      if (dueDateFilter.start && iso < dueDateFilter.start) matchesDueDate = false;
      if (dueDateFilter.end && iso > dueDateFilter.end) matchesDueDate = false;
    }

    // Total amount range filter
    let matchesTotal = true;
    const tot = Number(invoice.total_amount || 0);
    if (totalRange.min !== '' && tot < Number(totalRange.min)) matchesTotal = false;
    if (totalRange.max !== '' && tot > Number(totalRange.max)) matchesTotal = false;

    // Overdue filter (overdue / not_overdue / all)
    let matchesOverdueFilter = true;
    if (filterOverdue !== 'all') {
      const cust = customers.find((c) => c.id === invoice.customer_id);
      const creditDays = cust?.credit_period_days ?? 30;
      const inv = new Date(invoice.invoice_date);
      inv.setDate(inv.getDate() + creditDays);
      const isOverdue = invoice.payment_status !== 'Paid' && new Date() > inv;
      if (filterOverdue === 'overdue') matchesOverdueFilter = isOverdue;
      else if (filterOverdue === 'not_overdue') matchesOverdueFilter = !isOverdue;
    }

    // Bucket filter (drill-in from dashboard aging widget)
    let matchesBucket = true;
    if (bucketFilter) {
      // Paid invoices have no aging bucket
      if (invoice.payment_status === 'Paid') {
        matchesBucket = false;
      } else {
        const invDate = new Date(invoice.invoice_date);
        const customer = customers.find((c) => c.id === invoice.customer_id);
        const creditDays = customer?.credit_period_days ?? 30;
        const dueDate = new Date(invDate);
        dueDate.setDate(dueDate.getDate() + creditDays);
        const days = Math.floor((new Date() - dueDate) / (1000 * 60 * 60 * 24));
        let bucket = 'current';
        if (days >= 0 && days <= 30) bucket = '0_30';
        else if (days >= 31 && days <= 60) bucket = '31_60';
        else if (days >= 61 && days <= 90) bucket = '61_90';
        else if (days > 90) bucket = 'over_90';
        matchesBucket = bucket === bucketFilter;
      }
    }

    return matchesSearch && matchesStatus && matchesCustomer && matchesSalesRep
      && matchesApproval && matchesOutstanding && matchesDate && matchesDueDate
      && matchesTotal && matchesOverdueFilter && matchesBucket;
  }).sort((a, b) => {
    // Sort by invoice_number descending (newest/highest first)
    const numA = parseInt(a.invoice_number) || 0;
    const numB = parseInt(b.invoice_number) || 0;
    return numB - numA;
  });

  // Apply column-header sort (overrides default invoice_number desc when active)
  const {
    sortedItems: sortedInvoices,
    sortKey: invSortKey,
    sortDir: invSortDir,
    requestSort: requestInvSort,
  } = useSortableData(filteredInvoices);

  // Calculate due date + overdue status for an invoice using customer's credit period
  const getOverdueInfo = (invoice) => {
    const invoiceDate = new Date(invoice.invoice_date);
    const today = new Date();
    const customer = customers.find((c) => c.id === invoice.customer_id);
    const creditDays = customer?.credit_period_days ?? 30;

    const dueDate = new Date(invoiceDate);
    dueDate.setDate(dueDate.getDate() + creditDays);

    const daysDiff = Math.floor((today - invoiceDate) / (1000 * 60 * 60 * 24));
    const overdueDays = daysDiff - creditDays;
    const isOverdue = overdueDays > 0 && invoice.payment_status !== 'Paid';
    const daysUntilDue = Math.max(0, -overdueDays);

    return {
      isOverdue,
      overdueDays: Math.max(0, overdueDays),
      dueDate,
      creditDays,
      daysUntilDue,
    };
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterCustomerId('all');
    setFilterSalesRepId('all');
    setFilterApproval('all');
    setFilterOverdue('all');
    setOutstandingOnly(false);
    setDateFilter({ start: '', end: '' });
    setDueDateFilter({ start: '', end: '' });
    setTotalRange({ min: '', max: '' });
    setBucketFilter('');
    window.history.replaceState({}, '', '/sales-invoices');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/dashboard'}
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
          >
            ← Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            💰 {(user?.view_scope === 'all' || user?.role === 'admin' || user?.role === 'super_admin') ? 'Sales Invoices' : 'My Sales Invoices'}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleExportExcel}
            variant="outline"
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            data-testid="export-excel-btn"
          >
            📤 Export to Excel
          </Button>
          <FindDuplicatesButton
            module="sales-invoices"
            labelFields={['invoice_number', 'customer_name', 'invoice_date', 'total_amount', 'currency']}
            deletePath={(r) => `sales/invoices/${encodeURIComponent(r.invoice_number)}`}
            onAfterDelete={fetchData}
          />
          {hasPermission(user, 'sales_invoices', 'create') && (
            <>
              <Button
                onClick={() => setShowExcelImport(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="import-excel-btn"
              >
                📥 Import from Excel
              </Button>
              <Button
                onClick={() => {
                  resetForm();
                  setEditingInvoice(null);
                  setShowForm(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="new-invoice-btn"
              >
                + New Invoice
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Sales Performance - users with sales_reports view permission */}
      {hasPermission(user, 'sales_reports', 'view') && (
        <SalesPerformanceTable salesPerformance={salesPerformance} userRole={user?.role} />
      )}

      {/* Excel Import Modal */}
      {showExcelImport && (
        <ExcelImport
          onImport={handleExcelImport}
          onClose={() => setShowExcelImport(false)}
          data-testid="excel-import-modal"
        />
      )}

      {/* Invoice Form */}
      {showForm && (
        <Card className="mb-6" data-testid="invoice-form-card">
          <CardHeader>
            <CardTitle>{editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="invoice-form">
              {/* Delivery Note picker — only when creating a new invoice */}
              {!editingInvoice && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3" data-testid="dn-picker-banner">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm">
                      <b>🚚 Pull from Delivery Note (optional)</b>
                      <div className="text-xs text-blue-900/70">
                        Pick an open DN to auto-fill customer + items + serial numbers. You'll only need to enter unit prices.
                      </div>
                    </div>
                    <div className="flex items-center gap-2 min-w-[280px]">
                      <SearchableSelect
                        value={linkedDnNumber}
                        onChange={(v) => applyDeliveryNote(v)}
                        options={openDeliveryNotes.map((d) => ({
                          value: d.dn_number,
                          label: `${d.dn_number} — ${d.customer_name}${d.customer_po_no ? ' · PO ' + d.customer_po_no : ''}`,
                        }))}
                        placeholder={openDeliveryNotes.length ? 'Select Delivery Note' : 'No open delivery notes'}
                        data-testid="dn-picker"
                      />
                      {linkedDnNumber && (
                        <button
                          type="button"
                          onClick={() => { setLinkedDnNumber(''); setFormData((p) => ({ ...p, do_no: '' })); }}
                          className="text-xs text-red-700 hover:underline"
                        >Clear</button>
                      )}
                    </div>
                  </div>
                  {linkedDnNumber && (
                    <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                      ✓ Linked to <b>{linkedDnNumber}</b> — DN will be marked as invoiced when you save.
                    </div>
                  )}
                </div>
              )}

              {/* Invoice Header */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Number *
                  </label>
                  <input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded"
                    required
                    placeholder="INV-2026-001"
                    data-testid="invoice-number-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Date *
                  </label>
                  <DateInputDMY value={formData.invoice_date}
 onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
 className="w-full p-2 border border-gray-300 rounded"
 required
 />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Status
                  </label>
                  <select
                    value={formData.payment_status}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_status: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                    <option value="Partially Paid">Partially Paid</option>
                  </select>
                </div>
              </div>

              {/* Customer & Sales Rep */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer *
                  </label>
                  <SearchableSelect
                    value={formData.customer_id}
                    onChange={(val) => handleCustomerChange(val)}
                    options={customers.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="Select Customer"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sales Representative *
                  </label>
                  <SearchableSelect
                    value={formData.sales_rep_id}
                    onChange={(val) => handleSalesRepChange(val)}
                    options={salesReps.map((r) => ({ value: r.id, label: r.name }))}
                    placeholder="Select Sales Rep"
                    required
                  />
                </div>
              </div>

              {/* Prepared By — the actual user who data-entered the invoice.
                  Defaults to the current logged-in user on Create. Editable
                  ONLY by admin / super_admin so a regular user cannot pretend
                  someone else prepared the invoice. */}
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prepared By
                      <span className="text-xs text-gray-500 font-normal ml-2">
                        (admin only — controls the signature shown as "Prepared by" on the printed invoice)
                      </span>
                    </label>
                    <SearchableSelect
                      value={formData.created_by || (editingInvoice ? '' : user?.id) || ''}
                      onChange={(val) => setFormData(prev => ({ ...prev, created_by: val }))}
                      options={salesReps.map((r) => ({ value: r.id, label: r.name }))}
                      placeholder="Select preparer (defaults to you)"
                      data-testid="invoice-prepared-by-select"
                    />
                  </div>
                </div>
              )}

              {/* Reference & Project (optional, prints on Tax Invoice) */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">DO No</label>
                  <input
                    type="text"
                    value={formData.do_no || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, do_no: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Delivery Order No"
                    data-testid="invoice-do-no-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PO No</label>
                  <input
                    type="text"
                    value={formData.po_no || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, po_no: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Purchase Order No"
                    data-testid="invoice-po-no-input"
                  />
                  {openAdvances.length > 0 && (
                    <div className="mt-2 p-2 border border-amber-300 bg-amber-50 rounded text-sm" data-testid="invoice-advance-suggestion">
                      <div className="font-semibold text-amber-800 mb-1">
                        💰 Auto-applied {openAdvances.length} advance{openAdvances.length > 1 ? 's' : ''} ({formatAmount(autoAppliedAmount)})
                      </div>
                      <div className="space-y-1">
                        {openAdvances.map((adv) => (
                          <div key={adv.id} className="flex items-center justify-between gap-2 bg-white border border-amber-200 rounded px-2 py-1" data-testid={`auto-applied-advance-${adv.id}`}>
                            <div className="text-xs text-gray-700">
                              <span className="font-medium">{formatAmount(adv.received_amount)}</span>
                              {' · '}{adv.payment_mode}
                              {adv.receipt_no ? ` · Rcpt #${adv.receipt_no}` : ''}
                              {' · '}{formatDateDMY(adv.received_date)}
                            </div>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                              ✓ Applied
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="text-[11px] text-amber-700 mt-1">
                        The advance amount has been added to the “Advance Received” field below. Change customer or PO to reverse.
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PO Date</label>
                  <DateInputDMY
                    value={formData.po_date || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, po_date: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                  <input
                    type="text"
                    value={formData.project || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, project: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Project name"
                    data-testid="invoice-project-input"
                  />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Invoice Items *
                  </label>
                  <Button type="button" onClick={addItem} size="sm" className="bg-green-600 hover:bg-green-700">
                    + Add Item
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={`item-row-${index}`} className="border border-gray-200 rounded p-3 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-8 gap-2">
                        {/* Part Number - SEARCHABLE FIELD */}
                        <div className="relative part-number-dropdown-container">
                          <input
                            type="text"
                            placeholder="Part Number"
                            value={partNumberSearchTerm[index] || item.part_number || ''}
                            onChange={(e) => handlePartNumberSearch(index, e.target.value)}
                            onFocus={() => setShowPartNumberDropdown(prev => ({ ...prev, [index]: true }))}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                          />
                          {showPartNumberDropdown[index] && getFilteredProductsByPartNumber(index).length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {getFilteredProductsByPartNumber(index).map((product) => (
                                <div
                                  key={product.id}
                                  onClick={() => handlePartNumberSelect(index, product)}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100"
                                >
                                  <div className="font-medium">{product.part_number}</div>
                                  <div className="text-xs text-gray-500">
                                    {product.name}
                                    {product.category && ` • ${product.category}`}
                                    {product.brand && ` • ${product.brand}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Product Name with Search */}
                        <div className="md:col-span-2 relative product-dropdown-container">
                          <input
                            type="text"
                            placeholder="Search Product *"
                            value={productSearchTerm[index] || item.product_name}
                            onChange={(e) => handleProductSearch(index, e.target.value)}
                            onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            required
                          />
                          {showProductDropdown[index] && getFilteredProducts(index).length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {getFilteredProducts(index).map((product) => (
                                <div
                                  key={product.id}
                                  onClick={() => handleProductSelect(index, product)}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100"
                                >
                                  <div className="font-medium">{product.name}</div>
                                  <div className="text-xs text-gray-500">
                                    {product.part_number && `Part #: ${product.part_number}`}
                                    {product.category && ` • ${product.category}`}
                                    {product.brand && ` • ${product.brand}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Division */}
                        <div>
                          <select
                            value={item.division}
                            onChange={(e) => handleItemChange(index, 'division', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                          >
                            <option value="">Division</option>
                            {divisions.map(div => (
                              <option key={div.name} value={div.name}>{div.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Category */}
                        <div>
                          <select
                            value={item.category}
                            onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                          >
                            <option value="">Category</option>
                            {categories
                              .filter(cat => !item.division || cat.parent_division === item.division)
                              .map(cat => (
                                <option key={cat.name} value={cat.name}>{cat.name}</option>
                              ))}
                          </select>
                        </div>

                        {/* Sub-Category - NEW FIELD */}
                        <div>
                          <select
                            value={item.sub_category || ''}
                            onChange={(e) => handleItemChange(index, 'sub_category', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                          >
                            <option value="">Sub-Category</option>
                            {subcategories
                              .filter(subcat => !item.category || subcat.parent_category === item.category)
                              .map(subcat => (
                                <option key={subcat.name} value={subcat.name}>{subcat.name}</option>
                              ))}
                          </select>
                        </div>

                        {/* Brand */}
                        <div>
                          <select
                            value={item.brand}
                            onChange={(e) => handleItemChange(index, 'brand', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            disabled={!item.category}
                          >
                            <option value="">Brand</option>
                            {brands
                              .filter(brand => item.category && brand.parent_category === item.category)
                              .map(brand => (
                                <option key={brand.name} value={brand.name}>{brand.name}</option>
                              ))}
                          </select>
                        </div>

                        {/* Model */}
                        <div>
                          <select
                            value={item.model || ''}
                            onChange={(e) => handleItemChange(index, 'model', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            disabled={!item.brand}
                          >
                            <option value="">Model</option>
                            {models
                              .filter(model => item.brand && model.parent_brand === item.brand)
                              .map(model => (
                                <option key={model.name} value={model.name}>{model.name}</option>
                              ))}
                          </select>
                        </div>

                        {/* Quantity */}
                        <div>
                          <input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            min="0.01"
                            step="0.01"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-2">
                        <div>
                          <input
                            type="number"
                            placeholder="Unit Price"
                            value={item.unit_price}
                            onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            min="0"
                            step="0.001"
                            required
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            value={item.total.toFixed(3)}
                            className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                            disabled
                            placeholder="Total"
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            placeholder="Warranty (months)"
                            value={item.warranty_months ?? 0}
                            onChange={(e) => handleItemChange(index, 'warranty_months', parseInt(e.target.value) || 0)}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            min="0"
                            step="1"
                            title="Customer warranty in months from invoice date"
                            data-testid={`invoice-warranty-months-${index}`}
                          />
                        </div>
                        <div>
                          {formData.items.length > 1 && (
                            <Button
                              type="button"
                              onClick={() => removeItem(index)}
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-600 hover:bg-red-50"
                            >
                              Remove Item
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Available serial numbers — dropdown picker + selected chips */}
                      {item.product_id && (() => {
                        const list = availableSerialsMap[item.product_id] || [];
                        const selected = Array.isArray(item.serial_numbers) ? item.serial_numbers : [];
                        const remaining = list.filter((sn) => !selected.includes(sn.serial_number));
                        return (
                          <div className="mt-3 p-2 rounded border border-blue-200 bg-blue-50/40" data-testid={`invoice-serials-block-${index}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-700">
                                Serial Numbers — {list.length} in stock
                              </span>
                              {selected.length > 0 && (
                                <span className="text-xs text-blue-700">
                                  Selected: <b>{selected.length}</b>
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <select
                                value=""
                                disabled={remaining.length === 0}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v) toggleItemSerial(index, v);
                                }}
                                className="flex-1 min-w-[200px] px-2 py-1 border rounded text-sm"
                                data-testid={`invoice-serial-select-${index}`}
                              >
                                <option value="">
                                  {remaining.length === 0
                                    ? (list.length === 0 ? '— No stock — add via Purchase Entries —' : '— All in-stock serials added —')
                                    : `+ Add serial number (${remaining.length} available)`}
                                </option>
                                {remaining.map((sn) => (
                                  <option key={sn.serial_number} value={sn.serial_number}>{sn.serial_number}</option>
                                ))}
                              </select>
                              {selected.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setFormData((prev) => {
                                    const items = [...prev.items];
                                    items[index] = { ...items[index], serial_numbers: [] };
                                    return { ...prev, items };
                                  })}
                                  className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                                  data-testid={`invoice-serials-clear-${index}`}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            {selected.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {selected.map((s) => (
                                  <span
                                    key={s}
                                    className="text-xs px-2 py-1 rounded bg-blue-600 text-white inline-flex items-center gap-1"
                                    data-testid={`invoice-serial-chip-${index}-${s}`}
                                  >
                                    {s}
                                    <button
                                      type="button"
                                      onClick={() => toggleItemSerial(index, s)}
                                      className="text-white/80 hover:text-white"
                                      title="Remove this serial"
                                      aria-label={`Remove ${s}`}
                                    >
                                      ✕
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t pt-4">
                {/* VAT Category override — empty = auto-classified on the
                    VAT Return (Standard Rated if VAT% > 0, else Zero Rated). */}
                <div className="mb-3 max-w-2xl ml-auto bg-amber-50 border border-amber-200 rounded p-2">
                  <label className="block text-xs font-semibold text-amber-900 mb-1">
                    VAT Return Category (NBR classification)
                  </label>
                  <select
                    value={formData.vat_category || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, vat_category: e.target.value }))}
                    className="w-full border border-amber-300 rounded px-2 py-1 text-sm bg-white"
                    data-testid="invoice-vat-category"
                  >
                    <option value="">Auto (based on VAT %)</option>
                    <option value="standard_rated">Line 1 — Standard rated sales</option>
                    <option value="gcc_registered">Line 2 — Sales to registered VAT payers in GCC</option>
                    <option value="domestic_reverse_charge">Line 3 — Domestic reverse charge</option>
                    <option value="zero_rated">Line 4 — Zero rated domestic sales</option>
                    <option value="exports">Line 5 — Exports</option>
                    <option value="exempt">Line 6 — Exempt sales</option>
                  </select>
                  <p className="text-xs text-amber-700 mt-1">
                    Leave on "Auto" unless this invoice belongs to a specific category (export, exempt customer, GCC VAT-registered, etc.).
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl ml-auto">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Subtotal:</span>
                    <span className="text-lg" data-testid="invoice-subtotal">{formatAmount(formData.subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">VAT ({formData.vat_percentage}%):</span>
                    <span className="text-lg">{formatAmount(formData.vat_amount)}</span>
                  </div>
                  <div className="flex justify-between items-center md:col-span-2">
                    <span className="font-medium">Total (incl. VAT):</span>
                    <span className="text-lg" data-testid="invoice-total-incl-vat">
                      {formatAmount((formData.subtotal || 0) + (formData.vat_amount || 0))}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-3 md:col-span-2">
                    <label htmlFor="invoice-discount" className="font-medium whitespace-nowrap">
                      Less: Discount:
                    </label>
                    <input
                      id="invoice-discount"
                      type="number"
                      min="0"
                      step="0.001"
                      value={formData.discount ?? 0}
                      onChange={(e) => {
                        const disc = parseFloat(e.target.value) || 0;
                        const subtotal = formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
                        const vat = subtotal * (formData.vat_percentage / 100);
                        const adv = parseFloat(formData.advance_payment) || 0;
                        const total = Math.max(0, subtotal + vat - disc - adv);
                        setFormData(prev => ({
                          ...prev,
                          discount: parseFloat(disc.toFixed(3)),
                          vat_amount: parseFloat(vat.toFixed(3)),
                          total_amount: parseFloat(total.toFixed(3)),
                        }));
                      }}
                      className="w-32 border rounded px-2 py-1 text-right"
                      data-testid="invoice-discount-input"
                    />
                  </div>
                  <div className="flex justify-between items-center gap-3 md:col-span-2">
                    <label htmlFor="advance-payment" className="font-medium whitespace-nowrap flex items-center gap-1">
                      Less: Advance Received:
                      {openAdvances.length > 0 && (
                        <span
                          className="inline-flex items-center text-amber-600"
                          title={`Auto-applied from ${openAdvances.length} unapplied advance${openAdvances.length > 1 ? 's' : ''} on this customer's PO. Change the PO to unlock.`}
                          data-testid="advance-payment-lock-icon"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                          </svg>
                        </span>
                      )}
                    </label>
                    <input
                      id="advance-payment"
                      type="number"
                      min="0"
                      step="0.001"
                      value={formData.advance_payment ?? 0}
                      readOnly={openAdvances.length > 0}
                      title={openAdvances.length > 0 ? 'This advance is auto-applied from a customer advance against this PO. Clear or change the PO number to edit manually.' : ''}
                      onChange={(e) => {
                        if (openAdvances.length > 0) return; // locked
                        const adv = parseFloat(e.target.value) || 0;
                        setFormData(prev => ({ ...prev, advance_payment: adv }));
                        // recalc — VAT on full subtotal; discount + advance both deducted AFTER VAT
                        const subtotal = formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
                        const vat = subtotal * (formData.vat_percentage / 100);
                        const disc = parseFloat(formData.discount) || 0;
                        const total = Math.max(0, subtotal + vat - disc - adv);
                        setFormData(prev => ({
                          ...prev,
                          advance_payment: parseFloat(adv.toFixed(3)),
                          vat_amount: parseFloat(vat.toFixed(3)),
                          total_amount: parseFloat(total.toFixed(3)),
                        }));
                      }}
                      className={`w-32 border rounded px-2 py-1 text-right ${openAdvances.length > 0 ? 'bg-amber-50 cursor-not-allowed' : ''}`}
                      data-testid="invoice-advance-payment-input"
                    />
                  </div>
                  <div className="flex justify-between items-center bg-blue-50 p-2 rounded md:col-span-2">
                    <span className="font-bold">Balance Payable:</span>
                    <span className="text-xl font-bold text-blue-600" data-testid="invoice-balance-payable">{formatAmount(formData.total_amount)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded"
                  rows="3"
                  placeholder="Additional notes..."
                />
              </div>

              {/* Form Actions */}
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingInvoice(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {((user?.view_scope || 'all') === 'all' || user?.role === 'admin' || user?.role === 'super_admin') ? 'All Invoices' : 'My Invoices'} ({filteredInvoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search and Filters — every column header has a matching filter */}
          <InvoiceFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            filterApproval={filterApproval}
            setFilterApproval={setFilterApproval}
            filterCustomerId={filterCustomerId}
            setFilterCustomerId={setFilterCustomerId}
            customers={customers}
            filterSalesRepId={filterSalesRepId}
            setFilterSalesRepId={setFilterSalesRepId}
            salesReps={salesReps}
            filterOverdue={filterOverdue}
            setFilterOverdue={setFilterOverdue}
            outstandingOnly={outstandingOnly}
            setOutstandingOnly={setOutstandingOnly}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            dueDateFilter={dueDateFilter}
            setDueDateFilter={setDueDateFilter}
            totalRange={totalRange}
            setTotalRange={setTotalRange}
            onClearFilters={clearFilters}
          />

          {/* Extra filters row removed — Customer dropdown + Outstanding-only
              are now part of the main InvoiceFilters component above. */}

          {/* Filtered totals strip — gives instant £ visibility of current view */}
          {filteredInvoices.length > 0 && (() => {
            const t = filteredInvoices.reduce((acc, inv) => {
              acc.amount += (inv.total_amount || 0);
              acc.received += (inv.received_amount || 0);
              acc.outstanding += Math.max(0, (inv.total_amount || 0) - (inv.received_amount || 0));
              return acc;
            }, { amount: 0, received: 0, outstanding: 0 });
            return (
              <div className="flex flex-wrap items-center gap-3 mb-4 px-3 py-2 rounded-md bg-blue-50 border border-blue-200" data-testid="invoice-filtered-totals">
                <span className="text-xs text-gray-700">
                  Filtered: <b>{filteredInvoices.length}</b> invoices
                </span>
                <span className="text-xs px-2 py-1 rounded bg-white border border-blue-200" data-testid="invoice-filtered-total">
                  Total Invoice: <b>{formatAmount(t.amount)}</b>
                </span>
                <span className="text-xs px-2 py-1 rounded bg-white border border-green-200 text-green-800" data-testid="invoice-filtered-received">
                  Received: <b>{formatAmount(t.received)}</b>
                </span>
                <span className="text-xs px-2 py-1 rounded bg-white border border-red-200 text-red-800" data-testid="invoice-filtered-outstanding">
                  Outstanding: <b>{formatAmount(t.outstanding)}</b>
                </span>
              </div>
            );
          })()}

          {bucketFilter && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-md border border-purple-200 bg-purple-50 text-sm" data-testid="aging-bucket-banner">
              <span className="text-purple-800">
                Showing: <strong>{({
                  current:  'Not yet due',
                  '0_30':   '0–30 days overdue',
                  '31_60':  '31–60 days overdue',
                  '61_90':  '61–90 days overdue',
                  over_90:  '90+ days overdue',
                })[bucketFilter] || bucketFilter}</strong>
              </span>
              <button
                onClick={() => {
                  setBucketFilter('');
                  window.history.replaceState({}, '', '/sales-invoices');
                }}
                className="ml-auto text-purple-700 hover:text-purple-900 text-xs font-medium underline"
                data-testid="aging-bucket-clear"
              >
                Clear bucket filter
              </button>
            </div>
          )}

          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📄</div>
              <p className="text-xl font-semibold text-gray-700 mb-2">
                {invoices.length === 0 ? 'No Invoices Yet' : 'No Matching Invoices'}
              </p>
              <p className="text-gray-500">
                {invoices.length === 0 
                  ? (hasPermission(user, 'sales_invoices', 'create') ? 'Create your first invoice to get started.' : 'You haven\'t created any invoices yet.')
                  : 'Try adjusting your search or filters.'
                }
              </p>
              {invoices.length > 0 && (
                <Button onClick={clearFilters} className="mt-4" variant="outline">
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                    <SortableTh sortKey="invoice_number" currentSortKey={invSortKey} currentSortDir={invSortDir} onSort={requestInvSort}>Invoice #</SortableTh>
                    <SortableTh sortKey="invoice_date" currentSortKey={invSortKey} currentSortDir={invSortDir} onSort={requestInvSort}>Date</SortableTh>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <SortableTh sortKey="customer_name" currentSortKey={invSortKey} currentSortDir={invSortDir} onSort={requestInvSort}>Customer</SortableTh>
                    <SortableTh sortKey="sales_rep_name" currentSortKey={invSortKey} currentSortDir={invSortDir} onSort={requestInvSort}>Sales Rep</SortableTh>
                    <SortableTh sortKey="total_amount" currentSortKey={invSortKey} currentSortDir={invSortDir} onSort={requestInvSort}>Total</SortableTh>
                    <SortableTh sortKey="payment_status" currentSortKey={invSortKey} currentSortDir={invSortDir} onSort={requestInvSort}>Status</SortableTh>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approval</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Overdue</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedInvoices.map((invoice) => (
                    <React.Fragment key={invoice.invoice_number}>
                      <tr className={`cursor-pointer ${invoice.cancellation_status === 'pending' ? 'bg-amber-50 hover:bg-amber-100 ring-1 ring-amber-200' : 'hover:bg-gray-50'}`} onClick={() => setExpandedInvoice(expandedInvoice === invoice.invoice_number ? null : invoice.invoice_number)}>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <span className="inline-block transform transition-transform" style={{ transform: expandedInvoice === invoice.invoice_number ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                            ▶
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{invoice.invoice_number}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDateDMY(invoice.invoice_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {(() => {
                            const info = getOverdueInfo(invoice);
                            return (
                              <span title={`Credit period: ${info.creditDays} days`}>
                                {formatDateDMY(info.dueDate)}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{invoice.customer_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{invoice.sales_rep_name}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatAmount(invoice.total_amount)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            invoice.payment_status === 'Paid' ? 'bg-green-100 text-green-800' :
                            invoice.payment_status === 'Partially Paid' ? 'bg-yellow-100 text-yellow-800' :
                            (invoice.payment_status === 'cancelled' || invoice.cancellation_status === 'cancelled') ? 'bg-slate-200 text-slate-700 line-through' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {invoice.payment_status}
                          </span>
                          {invoice.cancellation_status === 'pending' && (
                            <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-semibold" title="A cancellation request is pending approval">
                              🟠 Cancel pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ApprovalStatusBadge status={invoice.approval_status || 'draft'} />
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const overdueInfo = getOverdueInfo(invoice);
                            if (overdueInfo.isOverdue) {
                              return (
                                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 font-semibold">
                                  ⚠️ {overdueInfo.overdueDays} days overdue
                                </span>
                              );
                            }
                            return <span className="text-xs text-gray-400">-</span>;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPrintingInvoice(invoice)}
                              className="text-purple-600 border-purple-600 hover:bg-purple-50"
                              data-testid={`invoice-print-row-${invoice.invoice_number}`}
                            >
                              🖨 Print
                            </Button>
                            {hasPermission(user, 'sales_invoices', 'edit') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(invoice)}
                                className="text-blue-600 border-blue-600 hover:bg-blue-50"
                              >
                                Edit
                              </Button>
                            )}
                            {hasPermission(user, 'sales_invoices', 'delete') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelete(invoice.invoice_number)}
                                className="text-red-600 border-red-600 hover:bg-red-50"
                              >
                                Delete
                              </Button>
                            )}
                            <RequestCancellationButton
                              docKind="sales_invoice"
                              docId={invoice.invoice_number}
                              doc={invoice}
                              onDone={fetchData}
                            />
                          </div>
                        </td>
                      </tr>
                      {/* Expandable Invoice Details */}
                      {expandedInvoice === invoice.invoice_number && (
                        <tr>
                          <td colSpan="10" className="px-4 py-4 bg-gray-50">
                            <div className="space-y-4">
                              <ApprovalBar
                                docKind="sales_invoice"
                                docId={invoice.invoice_number}
                                status={invoice.approval_status || 'draft'}
                                lastRejectionReason={invoice.last_rejection_reason}
                                onChange={() => fetchData()}
                              />
                              {invoice.cancellation_status === 'pending' && (
                                <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm" data-testid={`cancel-pending-banner-${invoice.invoice_number}`}>
                                  <div className="font-semibold text-amber-900">🟠 Cancellation pending approval</div>
                                  <div className="text-amber-800 mt-1"><span className="font-medium">Reason:</span> {invoice.cancellation_reason || '—'}</div>
                                  {invoice.cancellation_note && (
                                    <div className="text-amber-800"><span className="font-medium">Note:</span> {invoice.cancellation_note}</div>
                                  )}
                                  {invoice.cancellation_requested_by_name && (
                                    <div className="text-xs text-amber-700 mt-1">Requested by {invoice.cancellation_requested_by_name}{invoice.cancellation_requested_at ? ` • ${formatDateDMY(invoice.cancellation_requested_at)}` : ''}</div>
                                  )}
                                </div>
                              )}
                              {invoice.cancellation_status === 'cancelled' && (
                                <div className="rounded border border-slate-300 bg-slate-100 px-3 py-2 text-sm" data-testid={`cancel-done-banner-${invoice.invoice_number}`}>
                                  <div className="font-semibold text-slate-800">🚫 Invoice cancelled</div>
                                  {invoice.cancellation_reason && (
                                    <div className="text-slate-700 mt-1"><span className="font-medium">Reason:</span> {invoice.cancellation_reason}</div>
                                  )}
                                </div>
                              )}
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2">Invoice Details</h4>
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="text-gray-600">Customer:</span>
                                      <span className="ml-2 font-medium">{invoice.customer_name}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Sales Rep:</span>
                                      <span className="ml-2 font-medium">{invoice.sales_rep_name}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Date:</span>
                                      <span className="ml-2 font-medium">{formatDateDMY(invoice.invoice_date)}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Payment Status:</span>
                                      <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                        invoice.payment_status === 'Paid' ? 'bg-green-100 text-green-800' :
                                        invoice.payment_status === 'Partially Paid' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                      }`}>
                                        {invoice.payment_status}
                                      </span>
                                    </div>
                                  </div>
                                  {invoice.notes && (
                                    <div className="mt-2 text-sm">
                                      <span className="text-gray-600">Notes:</span>
                                      <p className="mt-1 text-gray-700">{invoice.notes}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Invoice Items Table */}
                              <div>
                                <h5 className="font-medium text-gray-900 mb-2">Items</h5>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full border border-gray-200">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Product</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Division</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Category</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Brand</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Model</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Qty</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Unit Price</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Total</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {invoice.items.map((item, idx) => (
                                        <React.Fragment key={`${invoice.invoice_number}-item-${item.product_id || item.product_name}-${idx}`}>
                                        <tr>
                                          <td className="px-3 py-2 text-sm text-gray-900">{item.product_name}</td>
                                          <td className="px-3 py-2 text-sm text-gray-600">{item.division || '-'}</td>
                                          <td className="px-3 py-2 text-sm text-gray-600">{item.category || '-'}</td>
                                          <td className="px-3 py-2 text-sm text-gray-600">{item.brand || '-'}</td>
                                          <td className="px-3 py-2 text-sm text-gray-600">{item.model || '-'}</td>
                                          <td className="px-3 py-2 text-sm text-right text-gray-900">{item.quantity}</td>
                                          <td className="px-3 py-2 text-sm text-right text-gray-900">{formatAmount(item.unit_price)}</td>
                                          <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">{formatAmount(item.total)}</td>
                                        </tr>
                                        {Array.isArray(item.serial_numbers) && item.serial_numbers.length > 0 && (
                                          <tr>
                                            <td colSpan="8" className="px-3 py-1 text-xs text-blue-700 bg-blue-50/40 border-t border-blue-100">
                                              <span className="font-semibold">S/N:</span> {item.serial_numbers.join(', ')}
                                            </td>
                                          </tr>
                                        )}
                                        </React.Fragment>
                                      ))}
                                    </tbody>
                                    <tfoot className="bg-gray-50">
                                      <tr>
                                        <td colSpan="7" className="px-3 py-2 text-sm text-right font-medium text-gray-700">Subtotal:</td>
                                        <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">{formatAmount(invoice.subtotal)}</td>
                                      </tr>
                                      <tr>
                                        <td colSpan="7" className="px-3 py-2 text-sm text-right font-medium text-gray-700">VAT ({invoice.vat_percentage}%):</td>
                                        <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">{formatAmount(invoice.vat_amount)}</td>
                                      </tr>
                                      <tr>
                                        <td colSpan="7" className="px-3 py-2 text-sm text-right font-medium text-gray-700">Total (incl. VAT):</td>
                                        <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">{formatAmount((invoice.subtotal || 0) + (invoice.vat_amount || 0))}</td>
                                      </tr>
                                      {(invoice.discount || 0) > 0 && (
                                        <tr>
                                          <td colSpan="7" className="px-3 py-2 text-sm text-right font-medium text-rose-700">Less: Discount:</td>
                                          <td className="px-3 py-2 text-sm text-right font-medium text-rose-700">− {formatAmount(invoice.discount)}</td>
                                        </tr>
                                      )}
                                      {(invoice.advance_payment || 0) > 0 && (
                                        <tr>
                                          <td colSpan="7" className="px-3 py-2 text-sm text-right font-medium text-emerald-700">Less: Advance Received:</td>
                                          <td className="px-3 py-2 text-sm text-right font-medium text-emerald-700">− {formatAmount(invoice.advance_payment)}</td>
                                        </tr>
                                      )}
                                      <tr className="border-t-2 border-gray-300">
                                        <td colSpan="7" className="px-3 py-2 text-sm text-right font-bold text-gray-900">Balance Payable:</td>
                                        <td className="px-3 py-2 text-sm text-right font-bold text-blue-600">{formatAmount(invoice.total_amount)}</td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {printingInvoice && (
        <InvoicePrintView
          invoice={printingInvoice}
          customer={customers.find((c) => c.id === printingInvoice.customer_id)}
          company={companySettings}
          formatAmount={formatAmount}
          watermark={
            (printingInvoice.approval_status || 'draft') === 'approved'
              ? null
              : (printingInvoice.approval_status === 'draft' ? 'DRAFT' : 'PENDING')
          }
          preparedBy={resolvePreparedBy(printingInvoice, salesReps)}
          approvers={resolveApprovers(printingInvoice, salesReps)}
          onClose={() => setPrintingInvoice(null)}
        />
      )}
    </div>
  );
}
